/**
 * The docs' remark plugin (runs after `remark-gfm` and `remark-directive`):
 *
 * 1. Every heading below the page title gets its anchor `id` from the shared slug rule
 *    (`shared/slug.ts`) — the same ids the build listed in "On this page".
 * 2. The docs' container directives become the docs' components (known form: remark-directive
 *    admonitions, as Docusaurus / VitePress write them):
 *      :::note / :::warning / :::tip [Title]      → Callout (design §0.4 roles)
 *      ::::steps                                   → Steps (each `###` heading is a step)
 *      ::::cards  with  :::card{title href}        → Cards
 *      ::::code-group  (fenced blocks inside)      → CodeGroup (one tab per block, languages synced)
 *      ::::tabs  with  :::tab{title}               → Tabs
 * 3. A text or leaf directive the docs do not know is put back as the text it was written as, so a
 *    word such as `art:x1` in a sentence is never swallowed; an unknown container keeps its content.
 * 4. A panel step — a bold path such as **Panel › Developers › Keys** (design §0.4) — links to that
 *    panel screen (`docs-panel-link`). The path resolves against the panel's one label list
 *    (`shared/panel.ts`); the build stops on a path that names no screen, so an unknown one never
 *    reaches here.
 */
import { createSlugger, plainText, TextNode } from "../shared/slug";
import { entryOfPath, panelUrl } from "../shared/panel";

interface Node extends TextNode {
  name?: string;
  depth?: number;
  attributes?: Record<string, string | null | undefined> | null;
  data?: { hName?: string; hProperties?: Record<string, unknown>; directiveLabel?: boolean };
  children?: Node[];
}

const CONTAINERS: Record<string, string> = {
  note: "docs-callout",
  warning: "docs-callout",
  tip: "docs-callout",
  steps: "docs-steps",
  cards: "docs-cards",
  card: "docs-card",
  "code-group": "docs-code-group",
  tabs: "docs-tabs",
  tab: "docs-tab",
};

const takeLabel = (node: Node): string | undefined => {
  const first = node.children?.[0];
  if (first?.data?.directiveLabel) {
    node.children = node.children?.slice(1);

    return plainText(first);
  }

  return undefined;
};

const asText = (node: Node): Node => {
  const prefix = node.type === "textDirective" ? ":" : "::";
  const label = node.children?.length ? `[${plainText(node)}]` : "";
  const text: Node = { type: "text", value: `${prefix}${node.name ?? ""}${label}` };

  return node.type === "textDirective" ? text : { type: "paragraph", children: [text] };
};

/** The words of a bold panel step, with line breaks and runs of spaces as single spaces. */
export const panelPathOf = (node: TextNode): string => plainText(node).replace(/\s+/g, " ").trim();

const transform = (node: Node, slug: (text: string) => string): Node => {
  if (node.type === "strong") {
    const entry = entryOfPath(panelPathOf(node));
    if (entry) node.data = { ...node.data, hName: "docs-panel-link", hProperties: { href: panelUrl(entry.id) } };
  }
  if (node.type === "heading" && (node.depth ?? 1) >= 2) {
    node.data = { ...node.data, hProperties: { ...(node.data?.hProperties ?? {}), id: slug(plainText(node)) } };
  }
  if (node.type === "containerDirective" && node.name && CONTAINERS[node.name]) {
    const label = takeLabel(node);
    const attributes = node.attributes ?? {};
    node.data = {
      ...node.data,
      hName: CONTAINERS[node.name],
      hProperties: {
        ...(["note", "warning", "tip"].includes(node.name) ? { role: node.name } : {}),
        ...(label ?? attributes.title ? { title: label ?? attributes.title } : {}),
        ...(attributes.href ? { href: attributes.href } : {}),
      },
    };
  } else if (node.type === "containerDirective") {
    node.data = { ...node.data, hName: "div" };
  } else if (node.type === "leafDirective" || node.type === "textDirective") {
    return asText(node);
  }
  if (node.children) node.children = node.children.map((child) => transform(child, slug));

  return node;
};

const remarkDocs = () => (tree: Node) => {
  transform(tree, createSlugger());
};

export default remarkDocs;
